import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { OriginalInputModel } from '@/domains/translate-bot/models/original-input-model';
import { SupportedLanguage } from '@/domains/translate-bot/enums';
import { OriginalInputService } from '../original-input-service';
import type { SupportedTargetLanguage } from '../../enums';
import type { OriginalInput, OriginalInputState } from '../../types';
import { Message } from '@patsnap/tahiti-core/common/message';
import { I18nService } from '@telecom/shared/services/i18n';

const LANGUAGE_CHECK_LENGTH = 5000;
const LANGUAGE_CHECK_PERCENT = 0.5;
const cnRange = [0x4e00, 0x9fa5];
const enRange = [0x21, 0x7e];
@injectable()
export class OriginalInputServiceImpl implements OriginalInputService {
    private _previousState: OriginalInputState | null = null;

    constructor(
        @inject(OriginalInputModel)
        private _model: OriginalInputModel,
        @inject(Message)
        private _message: Message,
        @inject(I18nService)
        private readonly _i18nService: I18nService,
    ) {
        this._model.onStatesChange(states => {
            if (this._previousState && states.input) {
                if (this._previousState.isSupportLang && !states.isSupportLang) {
                    this._message.warning({
                        message: this._i18nService.t('aiTranslation.noSupportLang'),
                        duration: 1500,
                        showClose: true,
                    });
                }
            }
            this._previousState = { ...states };
        });
    }

    // eslint-disable-next-line complexity
    async updateInput(input: string) {
        if (input.length === 0) {
            const value: Partial<OriginalInput> = { input, isSupportLang: true, targetLang: SupportedLanguage.EN };
            this._setModelData(value);
            return Promise.resolve(value);
        }

        // 校验语言
        const inputStr = input.slice(0, LANGUAGE_CHECK_LENGTH);
        let cnCount = 0;
        let enCount = 0;
        let otherCount = 0;

        for (let i = 0; i < inputStr.length; i++) {
            const charCode = inputStr.charCodeAt(i);

            if (charCode >= cnRange[0] && charCode <= cnRange[1]) {
                cnCount++;
            } else if (
                charCode >= enRange[0] && charCode <= enRange[1]
            ) {
                enCount++;
            } else {
                otherCount++;
            }
        }

        const total = cnCount + enCount + otherCount;

        let value: Partial<OriginalInput> = {
            input,
            isSupportLang: false,
        };

        if (cnCount / total > LANGUAGE_CHECK_PERCENT) {
            value = { input, isSupportLang: true, targetLang: SupportedLanguage.EN, sourceLang: SupportedLanguage.CN };
        }
        if (enCount / total > LANGUAGE_CHECK_PERCENT) {
            value = { input, isSupportLang: true, targetLang: SupportedLanguage.CN, sourceLang: SupportedLanguage.EN };
        }

        this._setModelData(value);

        return Promise.resolve(value);
    }

    public updateTargetLang(targetLang: SupportedTargetLanguage | null) {
        this._setModelData({ targetLang });
    }

    private _setModelData(value: Partial<OriginalInputState>) {
        this._model.update(modelData => {
            return { ...modelData, ...value };
        });
    }
}
